import { Injectable } from "@angular/core";
import { AuthSession } from "./auth-session";
import { AuthStorage } from "./auth.storage";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { SupabaseService } from "./supabase/supabase.service";
import { AuthChangeEvent, AuthError, AuthResponse, AuthTokenResponsePassword, Session } from "@supabase/supabase-js";
import { firstValueFrom, Subscription, take } from "rxjs";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { getProfileImageRoute } from "@ponder-features/user/profile/profile-image-router";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GetUserProfileResponse } from "@apiserver-rest/ponder-data-client-base";
import { UserRoleType } from "@apiserver-rest/ponder-auth-client-base";
import { RoleAndPermissions } from "./role-permissions";

/**
 * Handles communication with the auth sever (via {@link PonderAuthClient}) and the local auth storage ({@link AuthStorage}).
 */
@Injectable({ providedIn: "root" })
export class AuthService {
    private authSession?: AuthSession;
    private sessionPromise?: Promise<AuthSession | undefined>;
    private rolePermissions?: RoleAndPermissions;

    private displayNameUpdatedSub!: Subscription;

    constructor(private ponderAuthClient: PonderAuthClient, private ponderDataClient: PonderDataClient, private supabase: SupabaseService, private events: EventBroker) { 
        this.displayNameUpdatedSub = this.events.profile.displayNameUpdatedEvent.subscribe({
            next: (displayName?: string) => {
                AuthStorage.setDisplayName(displayName);

                if (this.authSession) {
                    this.authSession.displayName = displayName;
                }
            }
        });
    }

    public ngOnDestroy(): void {
        this.displayNameUpdatedSub.unsubscribe();
    }

    public async loginAsync(email: string, password: string): Promise<AuthTokenResponsePassword> {
        var response = await this.supabase.login(email, password);
        
        if (response.error) {
            return response;
        }
        
        this.sessionPromise = undefined;
        await this.getSessionAsync();

        if (this.authSession) {
            this.ponderAuthClient.accountConfirm();     // Fire and forget !-- Temporary until the proper domain is up and we can redirect to a confirm page --!
            this.events.auth.loginSuccessfulEvent.emit(this.authSession!);
        }

        return response;
    }

    public async signupAsync(email: string, username: string, password: string): Promise<AuthResponse> {
        var available = await firstValueFrom(this.ponderAuthClient.accountReserve(email, username));
        
        if (!available || !available.success) {
            return { 
                data: { user: null, session: null }, 
                error: new AuthError("Email and/or username unavailable.") 
            };
        }

        return await this.supabase.signup(email, password, username);
    }

    /**
     * Logs the user out and clears the local active session.
     */
    public async logOutAsync(emitEvents: boolean = true): Promise<void> {
        await this.supabase.logout();
        AuthStorage.clearStorage();

        if (emitEvents) {
            this.events.auth.logoutSuccessfulEvent.emit();
        }

        this.sessionPromise = undefined;
    }

    public async getSessionAsync(): Promise<AuthSession | undefined> {
        if (!this.sessionPromise) {
            this.sessionPromise = new Promise(async (resolve, reject) => {
                if (AuthStorage.isTempSession()) {
                    await this.logOutAsync(false);
                }

                if (!this.authSession) {
                    AuthStorage.populateFromSb();
    
                    const token = AuthStorage.getToken();
                    const sessionId = AuthStorage.getSessionId();
                    const username = AuthStorage.getUsername();
                    const email = AuthStorage.getEmail();
    
                    this.authSession = (token && sessionId && username && email) ? { 
                        token: token, 
                        id: sessionId,
                        username: username,
                        email: email,
                        avatarUrl: getProfileImageRoute(username),
                        displayName: AuthStorage.getDisplayName()
                    } : undefined;
                }
    
                if (this.authSession?.username && !this.authSession?.displayName) {
                    this.ponderDataClient
                        .userProfileGet(this.authSession.username)
                        .pipe(take(1))
                        .subscribe({
                            next: (response: GetUserProfileResponse) => {
                                if (response.success && response.userProfile?.user?.displayName) {
                                    this.authSession!.displayName = response.userProfile.user.displayName;
                                    AuthStorage.setDisplayName(this.authSession!.displayName);
                                }
                            }
                        })
                        .add(() => resolve(this.authSession));
                } else {
                    resolve(this.authSession);
                }
            });
        }

        return this.sessionPromise;        
    }

    public resetSession(): void {
        this.sessionPromise = undefined;
    }

    /**
     * @returns True if the current user is logged in.
     */
    public isAuthed(): boolean {
        return (this.authSession?.username) ? true : false;
    }

    /**
     * When an auth change occurs, we update our local auth session.
     */
    public registerAuthChangeCallback(): void {
        this.supabase.authChanges((authEvent: AuthChangeEvent, session: Session | null) => {
            this.getSessionAsync().finally(() => {});
        });
    }

    public async changePasswordAsync(oldPassword: string, newPassword: string): Promise<[boolean, string | undefined]> {
        const validPassword = await this.supabase.verifyPasswordAsync(oldPassword);

        if (!validPassword) {
            return [false, "Incorrect password."];
        }
        
        const changeResult = await this.supabase.changePasswordAsync(oldPassword, newPassword);

        if (changeResult) {
            return [true, undefined];
        } else {
            return [false, "Failed to update password."];
        }
    }

    public async resetPasswordAsync(newPassword: string): Promise<[boolean, string | undefined]> {
        const changeResult = await this.supabase.resetPasswordAsync(newPassword);

        if (changeResult) {
            return [true, undefined];
        } else {
            return [false, "Failed to update password."];
        }
    }

    public async changeUsernameAsync(newUsername: string): Promise<boolean> {
        var result = await this.supabase.changeUsernameAsync(newUsername, this.authSession!.id);
        
        if (result) {
            await this.logOutAsync();
        }

        return result;
    }

    public superSecretGetSessionDontUseMeUnlessYouHaveToOk(): AuthSession | undefined {
        return this.authSession;
    }

    public async sendPasswordRecoveryEmailAsync(email: string): Promise<void> {
        await this.supabase.sendPasswordRecoveryEmailAsync(email);
    }

    // -------------------------------------------------------------------------------------
    // Roles and Permissions
    // -------------------------------------------------------------------------------------
    
    public async getRoleAndPermissions(): Promise<RoleAndPermissions | undefined> {
        if (!this.isAuthed() || !this.authSession?.username) {
            return undefined;
        }

        this.rolePermissions = this.rolePermissions || AuthStorage.getRolePermissions();
        
        if (this.rolePermissions && !this.rolePermissions.isExpired()) {
            return this.rolePermissions;
        }

        this.rolePermissions = undefined;

        var response = await firstValueFrom(this.ponderAuthClient.listRolePermission(this.authSession.username));

        if (response.success) {
            this.rolePermissions = new RoleAndPermissions((response.role as string) as UserRoleType, response.permissions, response.resourceLimits);
            AuthStorage.setRoleAndPermissions(this.rolePermissions);
        }
        
        return this.rolePermissions;
    }
}